import { useMemo, useState } from "react";
import { Link2, Lock, Plus, Wallet } from "lucide-react";
import { Button } from "../../client/components/ui/button";
import useTranslation from "../../client/hooks/useTranslation";
import { useLiteData, type LiteAccount } from "../data/LiteDataProvider";
import { PageBody, PageHeader } from "../shell/LiteLayout";
import { useLiteActions } from "../shell/LiteActions";
import { EmptyStart } from "../onboarding/EmptyStart";
import { AccountCard } from "../ui/AccountCard";
import { Empty, FirmMark, Segmented, Stat, cardCls } from "../ui/ui";
import { LiteTradovateSync } from "./accounts/LiteTradovateSync";
import TradovateDiscoveredBanners from "../../trading-accounts/tradovate/TradovateDiscoveredBanners";

type Show = "active" | "inactive" | "all";

// Au-delà, on regroupe les cartes par prop firm (36 comptes dans un cas réel).
const GROUP_FROM = 7;

export default function LiteAccountsPage() {
  const t = useTranslation().lite.accounts;
  const { accounts, inScope, fmt, hiddenAccountsCount, loading } = useLiteData();
  const { openAddAccount, openConnectBroker, openAddToConnection, openTradovateImport, openUpgrade } = useLiteActions();
  const [show, setShow] = useState<Show>("active");

  // Filtres compte / prop firm de la barre commune (pas de période ici).
  const scoped = useMemo(() => accounts.filter(a => inScope(a)), [accounts, inScope]);

  const activeCount = scoped.filter(a => a.active).length;
  const inactiveCount = scoped.length - activeCount;
  const spent = scoped.reduce((s, a) => s + a.expensesTotal, 0);
  const paid = scoped.reduce((s, a) => s + a.payoutsTotal, 0);
  const visible = scoped.filter(a => show === "all" || (show === "active") === a.active);

  const addBtn = (
    <Button onClick={openAddAccount}><Plus className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">{t.addAccount}</span></Button>
  );

  return (
    <>
      <PageHeader
        title={t.title}
        subtitle={t.subtitle}
        filters="noPeriod"
        actions={
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="outline" onClick={openConnectBroker} aria-label={t.connectTradovate}>
              <Link2 className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">{t.connectTradovate}</span>
            </Button>
            {addBtn}
          </div>
        }
      />
      <PageBody>
        {hiddenAccountsCount > 0 && (
          <div className={`${cardCls} px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3`}>
            <Lock size={16} className="text-warning shrink-0" />
            <p className="text-sm text-muted-foreground flex-1">{t.hidden.text(hiddenAccountsCount)}</p>
            <Button size="sm" variant="outline" onClick={openUpgrade}>{t.hidden.cta}</Button>
          </div>
        )}

        {!accounts.length && !loading ? (
          <EmptyStart icon={Wallet} title={t.empty.title} text={t.empty.text} cta={<Button onClick={openAddAccount}><Plus className="h-4 w-4 mr-1.5" />{t.addAccount}</Button>} />
        ) : (
          <>
            <div className={`${cardCls} p-5 grid grid-cols-2 md:grid-cols-4 gap-6`}>
              <Stat label={t.summary.active} value={activeCount} hint={t.summary.inactiveHint(inactiveCount)} />
              <Stat label={t.summary.spent} value={fmt.money(-spent)} hint={t.summary.spentHint} />
              <Stat label={t.summary.payouts} value={fmt.money(paid)} tone="gain" />
              <Stat label={t.summary.balance} value={fmt.money(paid - spent)} tone={paid - spent >= 0 ? "gain" : "loss"} hint={t.summary.balanceHint} />
            </div>

            {/* Nouveaux comptes détectés chez le broker : même bandeau que l'Advanced. */}
            <TradovateDiscoveredBanners onImport={openTradovateImport} className="rounded-2xl" />
            <LiteTradovateSync onConnectNewPropFirm={openConnectBroker} onAddAccountToConnection={openAddToConnection} />

            <div>
              <Segmented<Show>
                value={show}
                onChange={setShow}
                options={[
                  { id: "active", label: t.show.active(activeCount) },
                  { id: "inactive", label: t.show.inactive(inactiveCount) },
                  { id: "all", label: t.show.all(scoped.length) },
                ]}
              />
            </div>

            {visible.length ? <AccountGrid accounts={visible} /> : (
              <div className={cardCls}><Empty title={t.noneHere}>{t.noneHereText}</Empty></div>
            )}
          </>
        )}
      </PageBody>
    </>
  );
}

function Grid({ accounts }: { accounts: LiteAccount[] }) {
  return (
    <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
      {accounts.map(a => <AccountCard key={a.id} account={a} />)}
    </div>
  );
}

/** Grille simple, ou regroupée par prop firm quand il y a beaucoup de comptes. */
function AccountGrid({ accounts }: { accounts: LiteAccount[] }) {
  const t = useTranslation().lite.accounts;
  const groups = useMemo(() => {
    const m = new Map<string, LiteAccount[]>();
    for (const a of accounts) m.set(a.firmKey, [...(m.get(a.firmKey) ?? []), a]);
    // Prop firms par ordre alphabétique, comptes perso en dernier.
    return [...m.values()].sort((x, y) => {
      if (!x[0].firm) return 1;
      if (!y[0].firm) return -1;
      return x[0].firm.name.localeCompare(y[0].firm.name);
    });
  }, [accounts]);

  if (accounts.length < GROUP_FROM || groups.length < 2) return <Grid accounts={accounts} />;
  return (
    <div className="space-y-6">
      {groups.map(g => (
        <section key={g[0].firmKey} className="space-y-3">
          <header className="flex items-center gap-2.5">
            <FirmMark firm={g[0].firm} size="sm" />
            <h2 className="font-display text-sm font-semibold">{g[0].firm?.name ?? t.personalGroup}</h2>
            <span className="text-xs text-muted-foreground">{t.groupCount(g.length)}</span>
          </header>
          <Grid accounts={g} />
        </section>
      ))}
    </div>
  );
}
